import type { Relations } from "@inrev/common";
import { createContext, useCallback, useContext, useMemo, useState } from "react";
import {
	type AssistantConversationSelection,
	useStoredAssistantConversation,
} from "./useStoredAssistantConversation";

export type AssistantSession = {
	conversationId: AssistantConversationSelection;
	/** Bumps each time the user opens a conversation, so the chat drops what it streamed. */
	openCount: number;
	/** The open chat has a question to leave behind and no answer running. */
	canStartNew: boolean;
	/** Shows the conversation, or a blank one for `"new"`. */
	openConversation: (conversationId: AssistantConversationSelection) => void;
	/** Records the id the open chat moved to on its own, e.g. the one its first send created. */
	setConversationId: (conversationId: AssistantConversationSelection) => void;
	setCanStartNew: (canStartNew: boolean) => void;
};

/** Which conversation an assistant host shows, kept per relation, shared by its header and chat. */
export const useAssistantSession = (relation: Relations.Notes): AssistantSession => {
	const [conversationId, setConversationId] = useStoredAssistantConversation(relation);
	const [openCount, setOpenCount] = useState(0);
	const [canStartNew, setCanStartNew] = useState(false);

	const openConversation = useCallback(
		(next: AssistantConversationSelection) => {
			setConversationId(next);
			setOpenCount((current) => current + 1);
		},
		[setConversationId],
	);

	return useMemo(
		() => ({
			conversationId,
			openCount,
			canStartNew,
			openConversation,
			setConversationId,
			setCanStartNew,
		}),
		[conversationId, openCount, canStartNew, openConversation, setConversationId],
	);
};

const AssistantSessionContext = createContext<AssistantSession | null>(null);

export const AssistantSessionProvider = AssistantSessionContext.Provider;

export const useAssistantSessionContext = () => {
	const context = useContext(AssistantSessionContext);
	if (!context) {
		throw new Error("useAssistantSessionContext must be used inside AssistantSessionProvider");
	}
	return context;
};
